/*
  Keepin' It Halal Brand - cart accessibility supplement (Section 28).

  Three gaps in the stock Dawn cart behaviour that Section 28 lists as required:

  1. "Screen-reader announcements for cart updates."
     Dawn re-renders the cart drawer by replacing the innerHTML of .drawer__inner
     inside #CartDrawer. The drawer's own live region (#CartDrawer-LiveRegionText)
     is inside that replaced subtree, so it is destroyed and recreated on every
     change. A live region that is removed and re-inserted does not announce, and
     Dawn only ever writes text into the cart *page* region
     (#cart-live-region-text), so a quantity change made from the drawer
     announced nothing at all.
     This script owns a persistent live region appended directly to <body>,
     outside every re-rendered subtree, and writes the new item count and total
     into it after each cart update.

     The region is created eagerly at init rather than lazily on first
     announcement. Several screen readers only track a live region that was
     present in the accessibility tree before its content changed, so a region
     inserted and populated in the same task can be missed entirely.

  2. "Cart drawer focus behavior" / Escape.
     Dawn binds Escape to a keyup listener on the <cart-drawer> element itself,
     so it only fires while focus is inside the drawer. Dawn's own
     CartItems.enableLoading() calls document.activeElement.blur() at the start
     of every quantity request, so for the whole duration of that request focus
     is on <body> and Escape does nothing. This script adds a document-level
     Escape handler that closes the drawer whenever it is open, wherever focus
     happens to be, and returns focus to the cart icon so the user is not
     stranded at the top of the document.

  3. Stranded focus on a failed cart request.
     Dawn blurs before the request and only restores focus in the success path.
     If the request errors, focus stays on <body> with no recovery. This script
     remembers what was focused when the blur happened and puts focus back if
     nothing else has claimed it.

  No cart data is changed here - this script only reads state and announces it.
*/
(function () {
  'use strict';

  var REGION_ID = 'kih-cart-live';

  /* ---------------------------------------------------------------- region */

  function makeRegion() {
    var el = document.getElementById(REGION_ID);
    if (el) return el;
    el = document.createElement('p');
    el.id = REGION_ID;
    el.className = 'visually-hidden';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    if (document.body) document.body.appendChild(el);
    return el;
  }

  /* ------------------------------------------------------------ readers */

  // Dawn's cart drawer renders the money value as .totals__total-value inside
  // a .totals block ("Estimated total"). Only the cart *page* footer uses
  // .totals__subtotal-value, and only in some Dawn configurations, so both are
  // listed. Ordered most specific first.
  var TOTAL_SELECTORS = [
    'cart-drawer .totals__total-value',
    'cart-drawer .totals__subtotal-value',
    '#main-cart-footer .totals__total-value',
    '#main-cart-footer .totals__subtotal-value',
    '.totals__total-value',
    '.totals__subtotal-value'
  ].join(', ');

  function clean(el) {
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function totalText() {
    return clean(document.querySelector(TOTAL_SELECTORS));
  }

  function totalLabel() {
    var el = document.querySelector('cart-drawer .totals__total, #main-cart-footer .totals__total, .totals__total');
    var label = clean(el);
    return label || 'Total';
  }

  function savingsText() {
    return clean(document.querySelector('.kih-cartprog__confirmed'));
  }

  /* --------------------------------------------------------- announcing */

  var lastSpoken = '';

  function announce(data) {
    var count = data && typeof data.item_count === 'number' ? data.item_count : null;
    var parts = [];

    if (count === 0) {
      parts.push('Your cart is empty.');
    } else if (count === 1) {
      parts.push('Cart updated. 1 item in your cart.');
    } else if (count !== null) {
      parts.push('Cart updated. ' + count + ' items in your cart.');
    } else {
      parts.push('Cart updated.');
    }

    if (count !== 0) {
      var total = totalText();
      if (total) parts.push(totalLabel() + ' ' + total + '.');

      var savings = savingsText();
      if (savings) parts.push(savings + '.');
    }

    var text = parts.join(' ');
    var el = makeRegion();
    // Clearing first guarantees the text is seen as a change even when the
    // resulting sentence is identical to the previous announcement.
    el.textContent = '';
    lastSpoken = text;
    window.setTimeout(function () {
      el.textContent = text;
    }, 60);
  }

  /* ------------------------------------------------------- pubsub binding */

  var bound = false;

  function bindCartUpdates() {
    if (bound) return true;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    if (!PUB_SUB_EVENTS || !PUB_SUB_EVENTS.cartUpdate) return false;
    bound = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      // The section HTML is swapped in synchronously after this event is
      // published, so read the rendered total on a later task.
      window.setTimeout(function () {
        announce(event && event.cartData);
      }, 320);
    });
    return true;
  }

  // pubsub.js and constants.js are deferred scripts, so on a cold load this
  // file can run before `subscribe` and `PUB_SUB_EVENTS` exist. The previous
  // version retried on DOMContentLoaded, which was dead code whenever init()
  // was itself called from DOMContentLoaded - the event had already fired, so
  // the listener never ran and the subscription was silently lost. Poll on a
  // short bounded interval instead.
  function bindCartUpdatesWithRetry() {
    if (bindCartUpdates()) return;
    var tries = 0;
    var timer = window.setInterval(function () {
      tries += 1;
      if (bindCartUpdates() || tries >= 40) window.clearInterval(timer);
    }, 100); // up to 4 seconds
    window.addEventListener('load', bindCartUpdates);
  }

  /* ------------------------------------------------------ drawer + focus */

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function drawerOpen() {
    var d = drawer();
    return !!(d && d.classList.contains('active'));
  }

  function bindEscape() {
    document.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'Escape' && event.code !== 'Escape') return;
        var d = drawer();
        if (!d || !d.classList.contains('active')) return;
        if (typeof d.close !== 'function') return;
        event.stopPropagation();
        d.close();
        var cartLink = document.getElementById('cart-icon-bubble');
        if (cartLink && typeof cartLink.focus === 'function') cartLink.focus();
      },
      true
    );
  }

  // Dawn blurs the active element before every quantity request and only
  // restores focus when the request succeeds. Remember the element so focus can
  // be recovered if the request fails and nothing else has taken focus.
  function bindFocusRecovery() {
    var lastInteractive = null;

    document.addEventListener(
      'focusin',
      function (event) {
        var t = event.target;
        if (t && t !== document.body && drawerOpen()) lastInteractive = t;
      },
      true
    );

    document.addEventListener(
      'blur',
      function () {
        if (!drawerOpen()) return;
        var candidate = lastInteractive;
        if (!candidate) return;
        window.setTimeout(function () {
          if (!drawerOpen()) return;
          if (document.activeElement && document.activeElement !== document.body) return;
          // Prefer the same control if it survived the re-render, otherwise the
          // first focusable control still inside the drawer.
          var target = document.contains(candidate) ? candidate : null;
          if (!target) {
            var d = drawer();
            target =
              d &&
              d.querySelector(
                '.drawer__close, button:not([disabled]), [href], input:not([disabled]), select:not([disabled])'
              );
          }
          if (target && typeof target.focus === 'function') target.focus();
        }, 2500);
      },
      true
    );
  }

  /* ------------------------------------------------------------------ init */

  function init() {
    makeRegion();
    bindEscape();
    bindFocusRecovery();
    bindCartUpdatesWithRetry();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
